import { ChromeLogin as Login } from '@/features/Login';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { ErrorBoundary } from '@sentry/react';
import { MaintenanceGate } from '@shared/components';
import { GlobalErrorBoundary } from '@shared/components/GlobalErrorBoundary';
import { PrivateRoute } from '@shared/components/PrivateRoute/PrivateRoute';
import { AnalyticsContextProvider } from '@shared/contexts/AnalyticsContext';
import { ApolloWrapper } from '@shared/contexts/ApolloWrapper';
import { AuthContextProvider } from '@shared/contexts/AuthContext';
import { BaseUrlContextProvider } from '@shared/contexts/BaseUrlContext';
import { ChatViewContextGetter } from '@shared/contexts/ChatWindowContext';
import { ExtensionMessagingContextProvider } from '@shared/contexts/ExtensionMessagingContext';
import { useMe } from '@shared/contexts/hooks/useMe';
import { MeContextProvider } from '@shared/contexts/MeContext';
import { StandardLogosContextProvider } from '@shared/contexts/pluginContexts/StandardLogosContextProvider';
import { useExtensionMessagingContext } from '@shared/contexts/useExtensionMessagingContext';
import { getPageHostname } from '@shared/features/page-content-permission/pageContentDomains';
import { PageContentPermissionPrompt } from '@shared/features/page-content-permission/PageContentPermissionPrompt';
import { usePageContentPermission } from '@shared/features/page-content-permission/usePageContentPermission';
import QuoteAuditTrail from '@shared/features/quote-trail/QuoteAuditTrail';
import { SidekickNavigator } from '@shared/features/sidekick-navigator/SidekickNavigator';
import { VoomaApiClient } from '@shared/generated/graphql';
import { useInterval } from '@shared/hooks/useInterval';
import { ToastProvider } from '@shared/plugin/contexts/ToastProvider';
import { AccountSettings } from '@shared/plugin/pages/AccountSettings';
import {
  PluginChatsPage,
  useDatEquipmentSearchChat,
} from '@shared/plugin/pages/Chats';
import { Quotes } from '@shared/plugin/pages/Quotes';
import { QuoteSidePanel } from '@shared/plugin/pages/QuoteSidePanel';
import { getGraphQLWebSocketUri } from '@shared/utils/apollo';
import {
  checkClientVersion,
  DEFAULT_VERSION_CHECK_INTERVAL,
} from '@shared/utils/version';
import { DrawerTabValue } from 'clerk_common/constants/urls/queryParams';
import {
  CHATS_ROUTE,
  QUOTES_ROUTE,
  TMS_SIDEKICK_ROUTE,
} from 'clerk_common/constants/urls/routes';
import { ChatViewPageContentAccess } from 'clerk_common/memories/getChatViewContext';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from '../../Layout';
import { Home } from '../Home/Home';
import { TMSSidekick } from '../TMSSidekick/TMSSidekick';

const WEB_BASE_URL = import.meta.env.VITE_WEB_BASE_URL;
const LOGIN_URL = `${import.meta.env.VITE_APP_BASE_URL}/login`;
const APOLLO_URI = `${import.meta.env.VITE_API_BASE_URL}/api`;
const APOLLO_WEBSOCKET_URI = getGraphQLWebSocketUri(APOLLO_URI);
const PAGE_SNAPSHOT_TIMEOUT_MS = 2_000;

export const Panel = () => {
  const [parentUrl, setParentUrl] = useState<string | undefined>(undefined);
  useInterval(() => {
    checkClientVersion();
  }, DEFAULT_VERSION_CHECK_INTERVAL);
  const location = useLocation();
  const privateRedirectRoute = `/login-prompt?parent=${parentUrl}`;

  return (
    <BaseUrlContextProvider url={WEB_BASE_URL}>
      <AnalyticsContextProvider apiClient={VoomaApiClient.SIDEKICK}>
        <ErrorBoundary fallback={GlobalErrorBoundary}>
          <AnimatePresence>
            <ToastProvider>
              <ExtensionMessagingContextProvider
                setParentUrl={setParentUrl}
                parentUrl={parentUrl ?? ''}
              >
                <Auth0ContextProvider>
                  <ApolloWrapper
                    appName="sidekick"
                    httpLinkUri={APOLLO_URI}
                    webSocketLinkUri={APOLLO_WEBSOCKET_URI}
                  >
                    <MeContextProvider useMe={useMe} enableFullStory={false}>
                      <MaintenanceGate isPlugin>
                        <StandardLogosContextProvider>
                          <SidekickNavigator>
                            <Routes location={location} key={location.pathname}>
                              <Route
                                path="/login-prompt"
                                element={
                                  <Login headerText="Automate freight workflows with Vooma" />
                                }
                              />
                              {/* Private routes */}
                              <Route
                                path="/"
                                element={
                                  <PrivateRoute
                                    redirectTo={privateRedirectRoute}
                                  >
                                    <Layout webBaseUrl={WEB_BASE_URL} hasMenu>
                                      <Home />
                                    </Layout>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={CHATS_ROUTE}
                                element={
                                  <PrivateRoute
                                    redirectTo={privateRedirectRoute}
                                  >
                                    <Layout webBaseUrl={WEB_BASE_URL} hasMenu>
                                      <SidekickChatsPage />
                                    </Layout>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={TMS_SIDEKICK_ROUTE}
                                element={
                                  <PrivateRoute
                                    redirectTo={privateRedirectRoute}
                                  >
                                    <Layout webBaseUrl={WEB_BASE_URL} hasMenu>
                                      <TMSSidekick />
                                    </Layout>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path="/settings"
                                element={
                                  <PrivateRoute
                                    redirectTo={privateRedirectRoute}
                                  >
                                    <Layout webBaseUrl={WEB_BASE_URL}>
                                      <AccountSettings />
                                    </Layout>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={QUOTES_ROUTE}
                                element={
                                  <PrivateRoute
                                    redirectTo={privateRedirectRoute}
                                  >
                                    <Layout webBaseUrl={WEB_BASE_URL}>
                                      <Quotes webBaseUrl={WEB_BASE_URL} />
                                    </Layout>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={`${QUOTES_ROUTE}/:quoteId`}
                                element={
                                  <PrivateRoute
                                    redirectTo={privateRedirectRoute}
                                  >
                                    <QuoteSidePanel webBaseUrl={WEB_BASE_URL} />
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path={`${QUOTES_ROUTE}/:quoteId/${DrawerTabValue.AUDIT_TRAIL}`}
                                element={
                                  <PrivateRoute
                                    redirectTo={privateRedirectRoute}
                                  >
                                    <Layout webBaseUrl={WEB_BASE_URL}>
                                      <QuoteAuditTrail />
                                    </Layout>
                                  </PrivateRoute>
                                }
                              />
                              <Route
                                path="*"
                                element={
                                  <GlobalErrorBoundary
                                    error={
                                      new Error('Issue navigating to page')
                                    }
                                  />
                                }
                              />
                            </Routes>
                          </SidekickNavigator>
                        </StandardLogosContextProvider>
                      </MaintenanceGate>
                    </MeContextProvider>
                  </ApolloWrapper>
                </Auth0ContextProvider>
              </ExtensionMessagingContextProvider>
            </ToastProvider>
          </AnimatePresence>
        </ErrorBoundary>
      </AnalyticsContextProvider>
    </BaseUrlContextProvider>
  );
};

function SidekickChatsPage() {
  const { parentUrl, refetchDom } = useExtensionMessagingContext();
  const {
    consumePageContentAccess,
    pendingHostname,
    preparePageContentAccess,
    respondToPendingRequest,
  } = usePageContentPermission();

  useEffect(() => {
    preparePageContentAccess(parentUrl || undefined);
  }, [parentUrl, preparePageContentAccess]);

  const getViewContext = useCallback(async () => {
    const pageUrl = parentUrl || undefined;
    const approvedHostname = consumePageContentAccess(pageUrl);
    const fetchedDom = approvedHostname
      ? await refetchDom(PAGE_SNAPSHOT_TIMEOUT_MS, {
          approvedHostname,
        }).catch(() => null)
      : null;
    const pageContentAccess = getPageHostname(pageUrl ?? '')
      ? approvedHostname
        ? ChatViewPageContentAccess.ALLOWED
        : ChatViewPageContentAccess.NOT_ALLOWED
      : undefined;
    return {
      entityIds: {},
      pageUrl,
      ...(pageContentAccess ? { pageContentAccess } : {}),
      ...(fetchedDom ? { content: fetchedDom.domContent } : {}),
    };
  }, [consumePageContentAccess, parentUrl, refetchDom]);

  const { pageChat, rememberConversation } =
    useDatEquipmentSearchChat(parentUrl);
  const getDatSuggestionContext: ChatViewContextGetter = () => ({
    entityIds: {},
    pageUrl: parentUrl,
  });

  const inputBanner = pendingHostname ? (
    <PageContentPermissionPrompt
      hostname={pendingHostname}
      onRespond={respondToPendingRequest}
    />
  ) : undefined;

  return (
    <PluginChatsPage
      getSuggestionContext={pageChat ? getDatSuggestionContext : undefined}
      getViewContext={getViewContext}
      inputBanner={inputBanner}
      onUserConversationSelect={rememberConversation}
      pageChat={pageChat}
    />
  );
}

type Auth0ContextProviderProps = {
  children: JSX.Element;
};
function Auth0ContextProvider({ children }: Auth0ContextProviderProps) {
  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      }}
    >
      <AuthProviderAdapter>{children}</AuthProviderAdapter>
    </Auth0Provider>
  );
}

function AuthProviderAdapter({ children }: { children: JSX.Element }) {
  const { logout } = useAuth0();

  const authLogout = async () => {
    await logout({
      logoutParams: {
        returnTo: `${import.meta.env.VITE_APP_BASE_URL}`,
      },
    });
  };

  return (
    <AuthContextProvider loginUrl={LOGIN_URL} authLogout={authLogout}>
      {children}
    </AuthContextProvider>
  );
}
